/**
 * Accounting Services › 1099 Compliance (payments by vendor for the year, who needs a 1099, W-9s
 * on file or requested through the portal) and the alerts for changes made in QuickBooks.
 */
import { formatCents } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { askText } from "@/components/dialogs";
import { Empty, Loading, StatRow } from "@/components/page";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { useToast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";

type VendorRow = {
  id: string;
  name: string;
  active: boolean;
  is1099: boolean;
  hasTaxId: boolean;
  reportableCents: number;
  cardCents: number;
  needs1099: boolean;
  review: boolean;
  w9: { receivedOn: string; documentId: string | null } | null;
  w9Request: { id: string; status: string; requestedAt: string; documents: Array<{ id: string; filename: string }> } | null;
};
type Data = { year: number; thresholdCents: number; thresholdPublished?: boolean; rows: VendorRow[]; needed: number; missingW9: number };

const field = "h-9 rounded-md border border-input bg-background px-2 text-sm";

export function Compliance1099({ clientId, me }: { clientId: string; me: Me }) {
  const { toast } = useToast();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(new Date().getMonth() <= 1 ? thisYear - 1 : thisYear);
  const { data, isLoading, error } = useQuery<Data>({ queryKey: [`/api/clients/${clientId}/books/1099?year=${year}`], retry: false });
  const canEdit = hasPermission(me, "clients.write");
  const canRequest = hasPermission(me, "requests.write");
  const record = useMutation({
    meta: { inlineError: true },
    mutationFn: (v: { id: string; name: string; documentId?: string }) => api.send("POST", `/api/clients/${clientId}/qbo/vendors/${v.id}/w9`, { vendorName: v.name, receivedOn: new Date().toLocaleDateString("en-CA"), ...(v.documentId ? { documentId: v.documentId } : {}) }),
    onSuccess: () => toast({ title: "W-9 recorded" }),
  });
  const request = useMutation({
    meta: { inlineError: true },
    mutationFn: (v: { id: string; name: string }) => api.send("POST", `/api/clients/${clientId}/books/1099/request-w9`, { vendorId: v.id, vendorName: v.name }),
    onSuccess: () => toast({ title: "W-9 requested", description: "The client is asked to upload it in the portal, with reminders." }),
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-muted-foreground">
          <span className="mb-1 block">Payments made in</span>
          <select aria-label="Year" className={field} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[thisYear, thisYear - 1, thisYear - 2].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        {data && <p className="text-sm text-muted-foreground">Form 1099-NEC is needed for {formatCents(data.thresholdCents)}{data.thresholdPublished === false ? ` (${data.year} amount not yet published)` : ""} or more paid by check, cash or bank transfer {data.year >= 2026 ? "(the threshold rose from $600 for payments made after 2025)" : ""}. Card payments, and payments through networks like PayPal, are reported by the processor on Form 1099-K instead; vendor refunds are subtracted. Corporations are exempt except for attorneys' fees and medical payments, and rent goes on Form 1099-MISC.</p>}
      </div>
      {isLoading ? (
        <Loading />
      ) : error || !data ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : (
        <>
          <StatRow
            stats={[
              { label: "Vendors needing a 1099", value: data.needed },
              { label: "Missing W-9", value: data.missingW9, tone: data.missingW9 ? "danger" : "success" },
              { label: "To review", value: data.rows.filter((r) => r.review).length, tone: data.rows.some((r) => r.review) ? "warning" : "default" },
            ]}
          />
          {(record.error || request.error) && <p className="text-sm text-destructive">{errorMessage(record.error ?? request.error)}</p>}
          {!data.rows.length ? (
            <Empty>No vendor payments in {data.year}, and no vendors are marked for 1099 in QuickBooks.</Empty>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {data.rows.map((v) => {
                const upload = v.w9Request?.documents[0];
                return (
                  <li key={v.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-sm">
                    <span className="min-w-0 flex-1 basis-full sm:basis-0">
                      <span className="block truncate font-medium">
                        {v.name}
                        {!v.active && <span className="ml-2 text-xs font-normal text-muted-foreground">inactive</span>}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {v.is1099 ? "Marked for 1099 in QuickBooks" : "Not marked for 1099"}
                        {v.is1099 && !v.hasTaxId && " · no tax ID in QuickBooks"}
                        {v.cardCents > 0 && ` · ${formatCents(v.cardCents)} paid by card (not counted)`}
                      </span>
                    </span>
                    <span className="tabular font-medium sm:w-28 sm:text-right">{formatCents(v.reportableCents)}</span>
                    <span className="sm:w-36">
                      {v.needs1099 ? (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">1099 needed</span>
                      ) : v.review ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200" title="Paid over the threshold but not marked for 1099. Corporations don't get one; confirm with the W-9.">
                          Review
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Under the threshold</span>
                      )}
                    </span>
                    <span className="flex w-full flex-wrap items-center gap-1 text-xs sm:w-64 sm:justify-end">
                      {v.w9 ? (
                        <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                          <CheckCircle2 className="h-3.5 w-3.5" /> W-9 received {formatDate(v.w9.receivedOn)}
                        </span>
                      ) : (
                        <>
                          {v.w9Request && (
                            <span className="text-muted-foreground">
                              {upload ? "Client uploaded the W-9" : `Requested ${formatDate(v.w9Request.requestedAt)}`}
                            </span>
                          )}
                          {canEdit && (v.is1099 || v.review) && (
                            <Button size="sm" variant={upload ? "default" : "ghost"} onClick={() => record.mutate({ id: v.id, name: v.name, ...(upload ? { documentId: upload.id } : {}) })}>
                              {upload ? "Record W-9" : "Record received"}
                            </Button>
                          )}
                          {canRequest && (v.is1099 || v.review) && !v.w9Request && (
                            <Button size="sm" variant="outline" onClick={() => request.mutate({ id: v.id, name: v.name })}>
                              Request W-9
                            </Button>
                          )}
                        </>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

type Change = { id: string; entity: string; entityId: string; operation: string; txnDate: string | null; amountCents: number | null; name: string; docNumber: string; changedAt: string; closedPeriod: boolean; acknowledgedAt: string | null; acknowledgement: string | null };

const ENTITY: Record<string, string> = { Purchase: "Expense", JournalEntry: "Journal entry", BillPayment: "Bill payment", SalesReceipt: "Sales receipt", CreditMemo: "Credit memo", RefundReceipt: "Refund", VendorCredit: "Vendor credit", Gap: "Change feed gap" };
const OP: Record<string, string> = { Create: "added", Update: "changed", Delete: "deleted", Void: "voided", Merge: "merged" };

/** Changes made in QuickBooks outside ClientFlow; those to closed months are alerts until acknowledged. */
export function QboAlerts({ clientId, me }: { clientId: string; me: Me }) {
  const { data } = useQuery<{ alerts: Change[]; recent: Change[]; readAt: string | null }>({ queryKey: [`/api/clients/${clientId}/books/qbo-alerts`], retry: false });
  const ack = useMutation({ meta: { inlineError: true }, mutationFn: (v: { id: string; note: string }) => api.send("POST", `/api/qbo-alerts/${v.id}/acknowledge`, { note: v.note }) });
  const canEdit = hasPermission(me, "engagements.write");
  if (!data) return null;
  const describe = (c: Change) => `${ENTITY[c.entity] ?? c.entity}${c.docNumber ? ` #${c.docNumber}` : ""}${c.name ? ` · ${c.name}` : ""}${c.amountCents !== null ? ` · ${formatCents(c.amountCents)}` : ""}`;
  return (
    <div className="space-y-3">
      {data.alerts.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
          <p className="flex items-center gap-2 text-sm font-semibold text-red-900 dark:text-red-200">
            <AlertTriangle className="h-4 w-4" /> {data.alerts.length} change{data.alerts.length > 1 ? "s" : ""} in QuickBooks to closed months
          </p>
          <ul className="mt-2 space-y-2">
            {data.alerts.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="min-w-0 flex-1">
                  {describe(c)} dated {c.txnDate ? formatDate(c.txnDate) : "—"}, {OP[c.operation] ?? c.operation} {formatDateTime(c.changedAt)}
                </span>
                {canEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={ack.isPending}
                    onClick={async () => {
                      const note = await askText({ title: "Acknowledge this change", description: "Say what was done: for example, reviewed with the client and corrected, or reopened the close.", label: "Note", required: true, confirmLabel: "Acknowledge", validate: (v) => (v.trim().length >= 3 ? null : "Write a short note") });
                      if (note) ack.mutate({ id: c.id, note });
                    }}
                  >
                    Acknowledge
                  </Button>
                )}
              </li>
            ))}
          </ul>
          {ack.error && <p className="mt-2 text-sm text-destructive">{errorMessage(ack.error)}</p>}
        </div>
      )}
      <details className="text-sm">
        <summary className="cursor-pointer text-muted-foreground">
          Changes made in QuickBooks outside ClientFlow ({data.recent.length}){data.readAt ? ` · checked ${formatDateTime(data.readAt)}` : ""}
        </summary>
        {!data.recent.length ? (
          <p className="mt-2 text-xs text-muted-foreground">None recorded. QuickBooks' change feed is read with every refresh.</p>
        ) : (
          <ul className="mt-2 divide-y divide-border rounded-lg border border-border">
            {data.recent.map((c) => (
              <li key={c.id} className={cn("flex flex-wrap gap-x-3 px-3 py-1.5 text-xs", c.closedPeriod && !c.acknowledgedAt && "text-red-800 dark:text-red-300")}>
                <span className="w-32 shrink-0">{formatDateTime(c.changedAt)}</span>
                <span className="min-w-0 flex-1">
                  {describe(c)} {OP[c.operation] ?? c.operation}
                  {c.txnDate && ` (dated ${formatDate(c.txnDate)})`}
                </span>
                {c.acknowledgement && <span className="text-muted-foreground">Acknowledged: {c.acknowledgement}</span>}
              </li>
            ))}
          </ul>
        )}
      </details>
    </div>
  );
}
